import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import haukeApiService from '../services/haukeApi';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import DirecterzeugerMap from '../components/DirecterzeugerMap';
import '../assets/css/direkterzeuger-admin.css';

const DirecterzeugerAdmin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [direkterzeuger, setDirekterzeuger] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(() => {
    return sessionStorage.getItem('direkterzeuger-admin-hasSearched') === 'true';
  });
  
  // Strukturierte Suchfelder - aus sessionStorage laden
  const [searchForm, setSearchForm] = useState(() => {
    const savedFilters = sessionStorage.getItem('direkterzeuger-admin-filters');
    if (savedFilters) {
      try {
        return JSON.parse(savedFilters);
      } catch (error) {
        console.warn('Fehler beim Laden gespeicherter Filter:', error);
      }
    }
    return {
      name: '',
      plz: '',
      city: '',
      membership: ''
    };
  });
  
  const [sortBy, setSortBy] = useState(() => {
    return sessionStorage.getItem('direkterzeuger-admin-sortBy') || 'name';
  });
  
  // Echte Cursor-Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageCursors, setPageCursors] = useState({}); // Map: page -> cursor
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  
  // Tab-System
  const [activeTab, setActiveTab] = useState('table');

  // hasSearched in sessionStorage speichern
  useEffect(() => {
    sessionStorage.setItem('direkterzeuger-admin-hasSearched', hasSearched.toString());
  }, [hasSearched]);

  // Filter in sessionStorage speichern, wenn sie sich ändern
  useEffect(() => {
    sessionStorage.setItem('direkterzeuger-admin-filters', JSON.stringify(searchForm));
  }, [searchForm]);

  // SortBy in sessionStorage speichern
  useEffect(() => {
    sessionStorage.setItem('direkterzeuger-admin-sortBy', sortBy);
  }, [sortBy]);

  // Prüfe beim Start, ob Filter gesetzt sind und lade automatisch
  useEffect(() => {
    const hasFilters = searchForm.name || searchForm.plz || searchForm.city || searchForm.membership;
    if (hasFilters && !hasSearched) {
      setHasSearched(true);
    }
  }, []);

  useEffect(() => {
    if (hasSearched) {
      fetchDirekterzeuger();
    }
  }, [currentPage, sortBy, hasSearched]);

  const fetchDirekterzeuger = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // HofId-Direktsuche: Wenn im Namensfeld eine plausible ID eingegeben wurde, versuche Detailabruf
      const idCandidate = (searchForm.name || '').trim();
      const looksLikeHofId = idCandidate && /^[A-Za-z0-9_-]{16,}$/.test(idCandidate);
      if (looksLikeHofId) {
        try {
          const details = await haukeApiService.getDirekterzeugerDetails(idCandidate);
          const record = details?.data;
          if (record && (record.id || record.hofId)) {
            const normalized = { ...record, id: record.id, hofId: record.hofId || record.id };
            setDirekterzeuger([normalized]);
            setTotalItems(1);
            setTotalPages(1);
            setHasNext(false);
            setHasPrev(false);
            setLoading(false);
            return;
          }
        } catch (_) {
          // Fallback auf normale Suche
        }
      }
      
      // Hole Cursor für aktuelle Seite
      const lastDocId = pageCursors[currentPage - 1] || '';
      
      console.log('SERVER-SIDE HaukeAPI Call:', {
        page: currentPage,
        limit: itemsPerPage,
        ...searchForm,
        sortBy: sortBy,
        lastDocId: lastDocId,
        userEmail: user?.email
      });
      
      // Echte server-seitige API mit Multi-Field-Suche
      const response = await haukeApiService.getDirekterzeuger({
        page: currentPage,
        limit: itemsPerPage,
        name: searchForm.name,
        plz: searchForm.plz,
        city: searchForm.city,
        membership: searchForm.membership,
        sortBy: sortBy,
        sortOrder: 'asc',
        lastDocId: lastDocId
      });
      
      console.log('SERVER-SIDE API Response:', response);
      
      // Server-Response verarbeiten
      const { data, pagination } = response;
      
      setDirekterzeuger(data || []);
      setTotalItems(pagination?.total || 0);
      setTotalPages(pagination?.totalPages || 0);
      setHasNext(pagination?.hasNext || false);
      setHasPrev(pagination?.hasPrev || false);
      
      // Speichere Cursor für nächste Seite
      if (pagination?.nextPageCursor && currentPage < totalPages) {
        setPageCursors(prev => ({
          ...prev,
          [currentPage]: pagination.nextPageCursor
        }));
      }
      
      console.log('SERVER-SIDE loaded direkterzeuger:', {
        count: data?.length || 0,
        total: pagination?.total || 0,
        page: currentPage,
        totalPages: pagination?.totalPages || 0,
        searchMode: response.searchMode || 'standard',
        nextCursor: pagination?.nextPageCursor
      });
      
    } catch (error) {
      console.error('Fehler beim Laden der Direkterzeuger:', error);
      setError(`Fehler beim Laden der Direkterzeuger: ${error.message}`);
      setDirekterzeuger([]);
    } finally {
      setLoading(false);
    }
  }

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchFormChange = (field, value) => {
    setSearchForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    setHasSearched(true);
    setCurrentPage(1); // Reset zu erster Seite bei Suche
    setPageCursors({}); // Reset Cursors bei neuer Suche
    fetchDirekterzeuger();
  };

  const handleClearSearch = () => {
    setSearchForm({
      name: '',
      plz: '',
      city: '',
      membership: ''
    });
    setHasSearched(false);
    setCurrentPage(1);
    setPageCursors({});
    setDirekterzeuger([]);
    setTotalItems(0);
    setTotalPages(0);
    
    // Gespeicherte Filter aus sessionStorage entfernen
    sessionStorage.removeItem('direkterzeuger-admin-filters');
    sessionStorage.removeItem('direkterzeuger-admin-sortBy');
    sessionStorage.removeItem('direkterzeuger-admin-hasSearched');
    setSortBy('name');
  };

  const handleClearField = (field) => {
    setSearchForm(prev => ({
      ...prev,
      [field]: ''
    }));
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setCurrentPage(1); // Reset zu erster Seite bei Sortierung
    setPageCursors({}); // Reset Cursors bei neuer Sortierung
  };

  const handleRowDoubleClick = (direkterzeuger) => {
    const hofId = direkterzeuger.hofId || direkterzeuger.id;
    if (hofId) {
      navigate(`/profil-admin/${hofId}`);
    }
  };

  const handleDeleteHof = async (direkterzeuger) => {
    const hofId = direkterzeuger.hofId || direkterzeuger.id;
    const hofName = direkterzeuger.name || 'Unbekannt';
    
    if (!hofId) {
      showErrorToast('Fehler: Keine Hof-ID gefunden');
      return;
    }

    if (window.confirm(`Möchten Sie den Hof "${hofName}" (ID: ${hofId}) wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      try {
        setLoading(true);
        
        // API-Aufruf zum Löschen des Hofs
        await haukeApiService.deleteHof(hofId);
        
        // Erfolg: Aktualisiere die Liste
        showSuccessToast(`Hof "${hofName}" wurde erfolgreich gelöscht.`);
        
        // Aktualisiere die aktuelle Suche
        if (hasSearched) {
          fetchDirekterzeuger();
        }
        
      } catch (error) {
        console.error('Fehler beim Löschen des Hofs:', error);
        showErrorToast(`Fehler beim Löschen des Hofs: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const hasActiveFilters = searchForm.name || searchForm.plz || searchForm.city || searchForm.membership;

  // Features: nur "Verkauf & Service" anzeigen
  const verkaufServiceFeatures = [
    { id: 'hofladen', label: 'Hofladen' },
    { id: 'lieferservice', label: 'Lieferservice' },
    { id: 'versand', label: 'Versand' },
    { id: 'selbstbedienung', label: 'Selbstbedienung' },
    { id: 'automat', label: 'SB-Automat' },
    { id: '24_7', label: '24/7 geöffnet' },
    { id: 'abokiste', label: 'Abokiste' },
    { id: 'online_shop', label: 'Online Shop' },
    { id: 'milchtankstelle', label: 'Milchtankstelle' },
    { id: 'selfpick', label: 'Selber Pflücken' }
  ];
  const allowedFeatureIds = new Set(verkaufServiceFeatures.map(f => f.id));
  const allowedFeatureLabels = new Set(verkaufServiceFeatures.map(f => f.label));
  const labelById = new Map(verkaufServiceFeatures.map(f => [f.id, f.label]));
  const getVerkaufServiceLabels = (features) => {
    if (!Array.isArray(features)) return [];
    const labels = features
      .map(f => String(f))
      .filter(f => allowedFeatureIds.has(f) || allowedFeatureLabels.has(f))
      .map(f => allowedFeatureIds.has(f) ? (labelById.get(f) || f) : f);
    return Array.from(new Set(labels));
  };

  const handleMapMarkerClick = (hofId) => {
    navigate(`/profil-admin/${hofId}`);
  };

  const handleMapRightClick = (latitude, longitude) => {
    // Navigiere zur AddDirekterzeuger Seite mit Koordinaten als Parameter
    navigate(`/direkterzeuger-admin/add?lat=${latitude}&lng=${longitude}`);
  };

  return (
    <div className="mantis-products">
      {/* Structured Search Form */}
      <div className="filters-section">
        <div className="search-form-container">
          <div className="search-form-header">
            <h3>Direkterzeuger durchsuchen</h3>
            <div className="search-form-actions">
              <button 
                className="btn btn-success"
                onClick={() => navigate('/direkterzeuger-admin/add')}
                disabled={loading}
              >
                <i className="material-icons">add</i>
                Anlegen
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSearch}
                disabled={loading}
              >
                <i className="material-icons">search</i>
                Suchen
              </button>
              <button 
                className="btn btn-outline"
                onClick={handleClearSearch}
                disabled={loading}
              >
                <i className="material-icons">clear</i>
                Zurücksetzen
              </button>
            </div>
          </div>
          
          
          <div className="search-form-grid">
            <div className="search-field">
              <label>Name</label>
              <div className="input-with-clear">
                <input
                  type="text"
                  placeholder="Name oder hofId..."
                  value={searchForm.name}
                  onChange={(e) => handleSearchFormChange('name', e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                {searchForm.name && (
                  <button
                    type="button"
                    className="clear-field-btn"
                    onClick={() => handleClearField('name')}
                    title="Feld leeren"
                  >
                    <i className="material-icons">clear</i>
                  </button>
                )}
              </div>
            </div>
            
            <div className="search-field">
              <label>PLZ</label>
              <div className="input-with-clear">
                <input
                  type="text"
                  placeholder="z.B. 10115, 80331..."
                  value={searchForm.plz}
                  onChange={(e) => handleSearchFormChange('plz', e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                {searchForm.plz && (
                  <button
                    type="button"
                    className="clear-field-btn"
                    onClick={() => handleClearField('plz')}
                    title="Feld leeren"
                  >
                    <i className="material-icons">clear</i>
                  </button>
                )}
              </div>
            </div>
            
            <div className="search-field">
              <label>Stadt</label>
              <div className="input-with-clear">
                <input
                  type="text"
                  placeholder="z.B. Berlin, München..."
                  value={searchForm.city}
                  onChange={(e) => handleSearchFormChange('city', e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                {searchForm.city && (
                  <button
                    type="button"
                    className="clear-field-btn"
                    onClick={() => handleClearField('city')}
                    title="Feld leeren"
                  >
                    <i className="material-icons">clear</i>
                  </button>
                )}
              </div>
            </div>
            
            <div className="search-field">
              <label>Mitgliedschaft</label>
              <div className="input-with-clear">
                <input
                  type="text"
                  placeholder="z.B. Premium, Standard..."
                  value={searchForm.membership}
                  onChange={(e) => handleSearchFormChange('membership', e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                {searchForm.membership && (
                  <button
                    type="button"
                    className="clear-field-btn"
                    onClick={() => handleClearField('membership')}
                    title="Feld leeren"
                  >
                    <i className="material-icons">clear</i>
                  </button>
                )}
              </div>
            </div>
          </div>
          

        </div>
        
        <div className="sort-controls">
          <label>Sortierung:</label>
          <select value={sortBy} onChange={(e) => handleSortChange(e.target.value)}>
            <option value="name">Nach Name</option>
            <option value="city">Nach Stadt</option>
            <option value="plz">Nach PLZ</option>
            <option value="category">Nach Kategorie</option>
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="products-content">
        {loading && (
          <div className="loading-state">
            <i className="material-icons">hourglass_empty</i>
            <p>Lade Direkterzeuger...</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <i className="material-icons">error</i>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && !hasSearched && (
          <div className="initial-state">
            <i className="material-icons">search</i>
            <h3>Direkterzeuger durchsuchen</h3>
            <p>Verwenden Sie die Suchfelder oben, um Direkterzeuger zu finden.</p>
            <p style={{ color: '#999', fontSize: '14px', marginTop: '8px' }}>
              Tipp: Sie können nach Name (auch Teilbereiche), PLZ, Stadt oder Mitgliedschaft suchen.
            </p>
          </div>
        )}

        {!loading && !error && hasSearched && (
          <div className="direkterzeuger-results-container">
            <div className="results-header">
              <h3>Direkterzeuger ({direkterzeuger.length} von {totalItems})</h3>
              {direkterzeuger.length === 0 && (
                <p style={{ color: '#677788', margin: '8px 0 0 0' }}>
                  Keine Direkterzeuger gefunden, die Ihren Suchkriterien entsprechen.
                </p>
              )}
            </div>
            
            {direkterzeuger.length > 0 && (
              <div className="view-tabs">
                <button 
                  className={`tab-button ${activeTab === 'table' ? 'active' : ''}`}
                  onClick={() => setActiveTab('table')}
                >
                  <i className="material-icons">table_chart</i>
                  Tabelle
                </button>
                <button 
                  className={`tab-button ${activeTab === 'map' ? 'active' : ''}`}
                  onClick={() => setActiveTab('map')}
                >
                  <i className="material-icons">map</i>
                  Karte
                </button>
              </div>
            )}
            
            {/* Tab Content */}
            {activeTab === 'table' && direkterzeuger.length > 0 && (
              <div className="table-wrapper">
                <table className="direkterzeuger-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Straße</th>
                    <th>PLZ</th>
                    <th>Stadt</th>
                    <th>E-Mail</th>
                    <th>Features</th>
                    <th>Mitgliedschaft</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {direkterzeuger.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="no-data">
                        {searchForm.name || searchForm.plz || searchForm.city || searchForm.membership ? 'Keine Direkterzeuger gefunden, die Ihren Suchkriterien entsprechen' : 'Keine Direkterzeuger gefunden'}
                      </td>
                    </tr>
                  ) : (
                    direkterzeuger.map((item, index) => {
                      const serviceLabels = getVerkaufServiceLabels(item.features || []);
                      return (
                        <tr 
                          key={index}
                          onDoubleClick={() => handleRowDoubleClick(item)}
                          style={{ cursor: 'pointer' }}
                          title="Doppelklick für Details"
                        >
                          <td>
                            <div className="producer-name">
                              <strong>{item.name || 'Unbekannt'}</strong>
                            </div>
                          </td>
                          <td>{item.street || '-'}</td>
                          <td>{item.plz || item.postal_code || '-'}</td>
                          <td>{item.city || '-'}</td>
                          <td>
                            {item.mail ? (
                              <i className="material-icons" style={{ color: '#2E7D32' }} title="E-Mail vorhanden">check_circle</i>
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                              {serviceLabels.length > 0 ? (
                                serviceLabels.map((label, i) => (
                                  <span
                                    key={i}
                                    style={{
                                      background: '#E8F5E9',
                                      color: '#2E7D32',
                                      padding: '2px 8px',
                                      borderRadius: '12px',
                                      fontSize: '12px',
                                      marginRight: '6px',
                                      marginBottom: '6px',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {label}
                                  </span>
                                ))
                              ) : (
                                <span>-</span>
                              )}
                            </div>
                          </td>
                          <td>
                            {item.membership ? (
                              <span className={`membership-badge ${item.membership}`}>
                                {item.membership}
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button 
                                className="btn-icon"
                                title="Details anzeigen"
                                onClick={() => console.log('Details:', item)}
                              >
                                <i className="material-icons">visibility</i>
                              </button>
                              <button 
                                className="btn-icon"
                                title="Bearbeiten"
                                onClick={() => console.log('Bearbeiten:', item)}
                              >
                                <i className="material-icons">edit</i>
                              </button>
                              <button 
                                className="btn-icon btn-danger"
                                title="Hof löschen"
                                onClick={() => handleDeleteHof(item)}
                              >
                                <i className="material-icons">delete</i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            )}
            
            {activeTab === 'map' && direkterzeuger.length > 0 && (
              <div className="map-wrapper">
                <div style={{ padding: '8px 12px', background: '#F0F7FF', borderBottom: '1px solid #E8ECF0', fontSize: '13px', color: '#1565C0' }}>
                  💡 Tipp: Rechtsklick auf die Karte um einen neuen Hof an dieser Position anzulegen
                </div>
                <DirecterzeugerMap 
                  direkterzeuger={direkterzeuger}
                  onMarkerClick={handleMapMarkerClick}
                  onRightClick={handleMapRightClick}
                />
              </div>
            )}
            
            {direkterzeuger.length === 0 && (
              <div className="no-results">
                <p>{searchForm.name || searchForm.plz || searchForm.city || searchForm.membership ? 'Keine Direkterzeuger gefunden, die Ihren Suchkriterien entsprechen' : 'Keine Direkterzeuger gefunden'}</p>
              </div>
            )}
            
            {/* Pagination - nur in Tabellenansicht */}
            {activeTab === 'table' && totalPages > 1 && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Zeige {((currentPage - 1) * itemsPerPage) + 1} bis {Math.min(currentPage * itemsPerPage, totalItems)} von {totalItems} Einträgen
                </div>
                <div className="pagination-controls">
                  <button 
                    className="btn btn-outline"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                  >
                    <i className="material-icons">first_page</i>
                  </button>
                  <button 
                    className="btn btn-outline"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <i className="material-icons">chevron_left</i>
                  </button>
                  
                  {/* Seiten-Nummern */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                    return (
                      <button
                        key={page}
                        className={`btn ${currentPage === page ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    );
                  })}
                  
                  <button 
                    className="btn btn-outline"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <i className="material-icons">chevron_right</i>
                  </button>
                  <button 
                    className="btn btn-outline"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    <i className="material-icons">last_page</i>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DirecterzeugerAdmin; 